'use strict';

import React, { useMemo, useState, useCallback, useRef } from 'react'

import PageWrapper from '@Layout/PageWrapper'
import Layout      from '@Layout/BackOffice'

import FormElement from '@/Form/toolkit/FormElement'
import DatePicker  from '@/Form/V2/DatePicker'
import Dashboard   from '@/Lists/Dashboard'
import BillRow     from '@/Lists/Rows/BillRow'
import Select      from '@/Form/V2/Select'

import SearchBar from '@Pure/SearchBar'
import Link      from '@Pure/Link'

import { bill_types, bill_payment_status } from '@Config/options'

import { isTrue } from '@root/utility'

const bill_types_options =
[
	{ label: 'Tous', value: 'all' }
]
.concat(bill_types);

const payment_status_options =
[
	{ label: 'Tous', value: 'all' }
]
.concat(bill_payment_status);

const DASHBOARD = 'BillsList';

const sortable_columns =
[
	'createdAt'
];

const header =
[
	{
		label: 'Date', name: 'date'
	},
	{
		label: 'Facture à', name: 'name'
	},
	{
		label: 'N° Facture', name: 'number'
	},
	{
		label: 'Type', name: 'type'
	},
	{
		label: 'N° commande', name: 'order_number'
	},
	{
		label: 'Montant', name: 'amount'
	},
	{
		label: 'Reste à régler', name: 'remaining_unpaid'
	},
	{
		label: '', name: 'PDF'
	}
]
.map(opt => ({ ...opt, sortable: sortable_columns.includes(opt.name) }));

const fields = header.map(data => data.name);

const fields_with_timeout =
[
	'searched', 'searched_number', 'date_from', 'date_to',
	'searched_by_amount'
];

const storage_filters =
{
	searched: window.localStorage.getItem(DASHBOARD + '.searched') || '',

	searched_number: window.localStorage
	.getItem(DASHBOARD + '.searched_number') || '',

	type: window.localStorage.getItem(DASHBOARD + '.type') || '',
	payment_status: window.localStorage.getItem(DASHBOARD + '.payment_status') || '',

	EC_renewal: isTrue(window.localStorage.getItem(DASHBOARD + '.EC_renewal')),
};

const NO_STATUS = { };

export default PageWrapper(function PaymentsDashboardPage (props)
{
	const search_timeout = useRef();

	const [instant_filters, setInstantFilters] = useState({ ...storage_filters });
	const [delayed_filters, setDelayedFilters] = useState({ ...storage_filters });

	const handleChange = useCallback(function ({ value, e, name })
	{
		if (!name) return;

		if (e === null && fields_with_timeout.includes(name) === false) return;

	// Prevent on mount trigger
		if (e === null && fields_with_timeout.includes(name)) return;

		window.localStorage.setItem(DASHBOARD + '.' + name, value);

		setInstantFilters(function (filters)
		{
			return { ...filters, [name]: value };
		});

		if (fields_with_timeout.includes(name) === false) return;

		if (search_timeout.current)
		{
			window.clearTimeout(search_timeout.current);
		}

		search_timeout.current = window.setTimeout(function ()
		{
			search_timeout.current = undefined;

			setDelayedFilters(function (filters)
			{
				return { ...filters, [name]: value };
			});
		},
		320);
	},
	[]);

	const handleSearchChange = useCallback(function (value, data={ }, e)
	{
		handleChange({ value: value, name: data?.name, e });
	},
	[handleChange]);

	const handleNativeChange = useCallback(function (e)
	{
		handleChange({ value: e?.target?.value, name: e?.target?.name, e });
	},
	[handleChange]);

	const handleFormElChange = useCallback(function (e, data)
	{
		handleChange({ e, ...data });
	},
	[handleChange]);

	const filters = useMemo(function ()
	{
		const filters =
		{
			searched: delayed_filters.searched,
			searched_number: delayed_filters.searched_number,
			payment_status: instant_filters.payment_status,
			date_from: delayed_filters.date_from,
			date_to: delayed_filters.date_to,
			type: instant_filters.type,
			searched_by_amount: delayed_filters.searched_by_amount,
			EC_renewal: instant_filters.EC_renewal
		};

		return filters;
	},
	[instant_filters, delayed_filters]);

	return (
		<Layout page_title="Factures" history={ props.history }>

			<div className="page-content">

				<div className="o-grid-4 u-flex-wrap u-content-between
					u-margin-bottom-15 u-items-end">

					<div className="o-grid__item u-flex u-items-end u-gap-10">
						<DatePicker name="date_from" label="Du"
							onChange={ handleChange }
							value={ instant_filters.date_from }
							bemit={ ['50%'] }
						/>

						<DatePicker name="date_to" label="Au"
							onChange={ handleChange }
							value={ instant_filters.date_to }
							bemit={ ['50%'] }
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="Facture à (CSE, Membre ou partenaire)"
							handleChange={ handleSearchChange }
							value={ instant_filters.searched }
							name="searched"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="Rechercher par identifiant"
							handleChange={ handleSearchChange }
							value={ instant_filters.searched_number }
							name="searched_number"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<SearchBar hide_btn={ true }
							placeholder="Par montant (ex 40.5)"
							handleChange={ handleSearchChange }
							value={ instant_filters.searched_by_amount }
							name="searched_by_amount"
							className="c-searchbar c-searchbar--dashboard"
						/>
					</div>

					<div className="o-grid__item">
						<Select onChange={ handleNativeChange }
							label="Type"
							value={ instant_filters.type }
							name="type"
							options={ bill_types_options }
						/>
					</div>

					<div className="o-grid__item">
						<Select onChange={ handleNativeChange }
							label="Réglé"
							value={ instant_filters.payment_status }
							name="payment_status"
							options={ payment_status_options }
						/>
					</div>

					<div className="o-grid__item u-flex u-items-center">
						<FormElement name="EC_renewal"
							type="checkbox" data={ instant_filters }
							onChange={ handleFormElChange }
							status={ NO_STATUS }
							label="Renouvellement CSE"
							className="u-margin-top-0"
						/>
					</div>
					<div className="o-grid__item" />
				</div>

				<div className="o-content">
					<div className="u-flex u-content-end u-margin-bottom-20">
						<Link href="/gestion-des-factures/nouvelle"
							className="c-btn c-btn--add u-no-margin-top">
							Nouvelle facture
						</Link>
					</div>
				</div>

				<div className="o-content">
					<Dashboard model="Bill" items-per-page={ 50 }
						url="/dashboard/bills" filters={ filters }
						header={ header } fields={ fields }
						order="DESC" order-by="number"
						disable-sort={ filters.searched || filters.searched_number
							? true : false }
						component={ BillRow }
					/>
				</div>
			</div>

		</Layout>
	);
});